/**
 * EvidenceChunks - Progressive disclosure evidence list.
 * Shows chunk text, source, date, and behavioral codes.
 * Supports expand/collapse per item and "Load more" pagination.
 */

import React, { useState } from 'react';
import { Box, Typography, Chip, IconButton, Button } from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { VALENCE_COLORS } from '../../../BehavioralWorkbench/constants/workbenchConfig';

const PAGE_SIZE = 5;

// eslint-disable-next-line max-lines-per-function
function EvidenceChunks({ chunks }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  if (!chunks || chunks.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No evidence available
      </Typography>
    );
  }

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleChunks = chunks.slice(0, visibleCount);

  return (
    <Box>
      {visibleChunks.map((chunk, index) => {
        const chunkId = chunk.id || chunk.chunk_id || `chunk-${index}`;
        const isExpanded = expandedIds.has(chunkId);
        const text = chunk.content || chunk.text || '';
        const previewText = text.length > 150 ? text.substring(0, 150) + '...' : text;
        const codes = chunk.codes || chunk.behavioral_codes || [];
        const date = chunk.created_at || chunk.timestamp;

        return (
          <Box
            key={chunkId}
            sx={{
              p: 1.5,
              mb: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              '&:hover': { bgcolor: 'grey.50' },
              transition: 'background-color 0.2s',
            }}
          >
            {/* Header row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.5 }}>
                  {isExpanded ? text : previewText}
                </Typography>
              </Box>
              {text.length > 150 && (
                <IconButton size="small" onClick={() => toggleExpand(chunkId)}>
                  {isExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              )}
            </Box>

            {/* Metadata */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
              {date && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(date).toLocaleDateString()}
                </Typography>
              )}
              {chunk.source_id && (
                <Typography variant="caption" color="text.secondary">
                  {chunk.source_id}
                </Typography>
              )}
            </Box>

            {/* Behavioral Codes */}
            {codes.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
                {codes.slice(0, 5).map((code, ci) => {
                  const codeName = typeof code === 'string' ? code : code.name || code.code;
                  const valence = typeof code === 'object' ? code.valence : null;
                  const valenceColor = valence
                    ? VALENCE_COLORS[valence] || VALENCE_COLORS.neutral
                    : VALENCE_COLORS.neutral;

                  return (
                    <Chip
                      key={`${chunkId}-code-${ci}`}
                      label={codeName}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.6rem',
                        bgcolor: `${valenceColor}15`,
                        border: `1px solid ${valenceColor}`,
                        color: valenceColor,
                      }}
                    />
                  );
                })}
                {codes.length > 5 && (
                  <Typography variant="caption" color="text.secondary">
                    +{codes.length - 5}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {/* Load More */}
      {visibleCount < chunks.length && (
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button
            size="small"
            onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
          >
            Load More ({chunks.length - visibleCount} remaining)
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default EvidenceChunks;
